import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import {
  defaultErrorResponses,
  problemResponse,
  registry,
  undeclaredQueryRefused,
} from "../registry.js";
import { z } from "../zod.js";
import { passwordChangeRequired } from "./register.js";
import {
  AuthProfile,
  AuthProfilePatchRequest,
  ChangePasswordRequest,
  Department,
  DepartmentCreateRequest,
  DepartmentListResponse,
  DepartmentPatchRequest,
  Facility,
  FacilityCreateRequest,
  FacilityListResponse,
  FacilityPatchRequest,
  Invite,
  InviteAcceptRequest,
  InviteAcceptResponse,
  InviteCreateRequest,
  InviteListResponse,
  InviteLookupResponse,
  MemberListItem,
  MemberListResponse,
  MemberRevokeRequest,
  MembershipCreateRequest,
  MembershipPatchRequest,
  MembershipView,
  PermissionListResponse,
  ProvisionMemberRequest,
  ProvisionMemberResponse,
  RevokedCountResponse,
  RoleCreateRequest,
  RoleDetail,
  RoleInUseProblem,
  RoleListResponse,
  RolePatchRequest,
  RolePermissionsPatchRequest,
  TenantPickerResponse,
  WorkCenter,
  WorkCenterCreateRequest,
  WorkCenterListResponse,
  WorkCenterPatchRequest,
} from "./admin-schemas.js";
import { SessionListResponse } from "./schemas.js";

// PR #6 — the tenancy/administration surface (spec 16 §5 subset). Every
// mutation below (outside /v1/auth/*) requires the Idempotency-Key header
// (TESTING §4.3); the generated client sends it per request.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission?: string;
  idempotent?: boolean;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["identity"],
    summary: config.summary,
    description:
      (config.description ?? "") +
      (config.permission !== undefined ? ` Requires \`${config.permission}\`.` : ""),
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation && config.idempotent !== false ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});

const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});

const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const IdParam = z.object({ id: z.guid() });
const UserIdParam = z.object({ userId: z.guid() });

// --- org tree ---

route({
  method: "get",
  path: "/v1/org/facilities",
  operationId: "listFacilities",
  summary: "List facilities",
  permission: "identity.view_org",
  request: { query: z.object({ status: z.enum(["active", "archived"]).optional() }) },
  responses: ok("Facilities", FacilityListResponse),
});
route({
  method: "post",
  path: "/v1/org/facilities",
  operationId: "createFacility",
  summary: "Create facility (group scope only)",
  permission: "identity.manage_org",
  request: jsonBody(FacilityCreateRequest),
  responses: created("Facility created", Facility),
});
route({
  method: "patch",
  path: "/v1/org/facilities/{id}",
  operationId: "updateFacility",
  summary: "Edit/archive facility (archive guarded while referenced — 409 FACILITY_IN_USE)",
  permission: "identity.manage_org",
  request: { params: IdParam, ...jsonBody(FacilityPatchRequest) },
  responses: ok("Facility updated", Facility),
});
route({
  method: "get",
  path: "/v1/org/facilities/{id}/departments",
  operationId: "listDepartments",
  summary: "List departments of a facility",
  permission: "identity.view_org",
  request: { params: IdParam },
  responses: ok("Departments", DepartmentListResponse),
});
route({
  method: "post",
  path: "/v1/org/departments",
  operationId: "createDepartment",
  summary: "Create department",
  permission: "identity.manage_org",
  request: jsonBody(DepartmentCreateRequest),
  responses: created("Department created", Department),
});
route({
  method: "patch",
  path: "/v1/org/departments/{id}",
  operationId: "updateDepartment",
  summary: "Edit/archive department (409 DEPARTMENT_IN_USE while referenced)",
  permission: "identity.manage_org",
  request: { params: IdParam, ...jsonBody(DepartmentPatchRequest) },
  responses: ok("Department updated", Department),
});
route({
  method: "get",
  path: "/v1/org/work-centers",
  operationId: "listWorkCenters",
  summary: "List work centers (rate fields only with a cost field-grant)",
  permission: "identity.view_org",
  request: {
    query: z.object({
      facility_id: z.guid().optional(),
      status: z.enum(["active", "archived"]).optional(),
    }),
  },
  responses: ok("Work centers", WorkCenterListResponse),
});
route({
  method: "post",
  path: "/v1/org/work-centers",
  operationId: "createWorkCenter",
  summary: "Create work center (spec 16 §2.19)",
  permission: "identity.manage_work_centers",
  request: jsonBody(WorkCenterCreateRequest),
  responses: created("Work center created", WorkCenter),
});
route({
  method: "patch",
  path: "/v1/org/work-centers/{id}",
  operationId: "updateWorkCenter",
  summary: "Edit/archive work center (409 WORK_CENTER_IN_USE on archive while referenced)",
  permission: "identity.manage_work_centers",
  request: { params: IdParam, ...jsonBody(WorkCenterPatchRequest) },
  responses: ok("Work center updated", WorkCenter),
});

// --- members ---

route({
  method: "get",
  path: "/v1/members",
  operationId: "listMembers",
  summary: "List members (facility-scoped admins see only their facilities')",
  permission: "identity.view_members",
  request: {
    query: z.object({
      role_id: z.guid().optional(),
      facility_id: z.guid().optional(),
      status: z.enum(["active", "revoked"]).optional(),
      q: z.string().max(200).optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok("Members", MemberListResponse),
});
route({
  method: "get",
  path: "/v1/members/{userId}",
  operationId: "getMember",
  summary: "Member detail",
  permission: "identity.view_members",
  request: { params: UserIdParam },
  responses: ok("Member", MemberListItem),
});
route({
  method: "post",
  path: "/v1/members/provision",
  operationId: "provisionMember",
  summary: "Create account directly with a temp password (spec 16 §3.5a)",
  permission: "identity.provision_member",
  request: jsonBody(ProvisionMemberRequest),
  responses: {
    ...created("Account provisioned — temp password returned once", ProvisionMemberResponse),
    409: problemResponse("ROLE_NOT_PROVISIONABLE / USER_EXISTS"),
  },
});
route({
  method: "post",
  path: "/v1/members/{userId}/memberships",
  operationId: "addMembership",
  summary: "Add a membership row (role × scope)",
  permission: "identity.update_member",
  request: { params: UserIdParam, ...jsonBody(MembershipCreateRequest) },
  responses: created("Membership added", MembershipView),
});
route({
  method: "patch",
  path: "/v1/members/{userId}/memberships/{id}",
  operationId: "updateMembership",
  summary: "Change a membership's role/scope",
  permission: "identity.update_member",
  request: { params: UserIdParam.extend(IdParam.shape), ...jsonBody(MembershipPatchRequest) },
  responses: ok("Membership updated", MembershipView),
});
route({
  method: "delete",
  path: "/v1/members/{userId}/memberships/{id}",
  operationId: "revokeMembership",
  summary: "Revoke one membership (409 LAST_GROUP_ADMIN guard)",
  permission: "identity.revoke_member",
  request: { params: UserIdParam.extend(IdParam.shape) },
  responses: { 204: { description: "Membership revoked" } },
});
route({
  method: "post",
  path: "/v1/members/{userId}/revoke",
  operationId: "revokeMember",
  summary: "Revoke ALL memberships + sessions here (leaver offboarding)",
  permission: "identity.revoke_member",
  request: { params: UserIdParam, ...jsonBody(MemberRevokeRequest) },
  responses: { 204: { description: "Member revoked" } },
});
route({
  method: "post",
  path: "/v1/members/{userId}/unlock",
  operationId: "unlockMember",
  summary: "Clear a member's login lockout",
  permission: "identity.unlock_account",
  request: { params: UserIdParam },
  responses: { 204: { description: "Lockout cleared" } },
});
route({
  method: "get",
  path: "/v1/members/{userId}/sessions",
  operationId: "listMemberSessions",
  summary: "A member's device list",
  permission: "identity.view_sessions_any",
  request: { params: UserIdParam },
  responses: ok("Sessions", SessionListResponse),
});
route({
  method: "post",
  path: "/v1/members/{userId}/sessions/revoke_all",
  operationId: "revokeMemberSessions",
  summary: "Kill all of a member's sessions",
  permission: "identity.revoke_session_any",
  request: { params: UserIdParam },
  responses: ok("Sessions revoked", RevokedCountResponse),
});

// --- invites ---

route({
  method: "get",
  path: "/v1/invites",
  operationId: "listInvites",
  summary: "List invites",
  permission: "identity.invite_member",
  request: {
    query: z.object({
      status: z.enum(["pending", "accepted", "expired", "revoked"]).optional(),
      email: z.string().optional(),
      facility_id: z.guid().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok("Invites", InviteListResponse),
});
route({
  method: "post",
  path: "/v1/invites",
  operationId: "createInvite",
  summary: "Create invite (scope-guarded, spec 16 §3.5.1)",
  permission: "identity.invite_member",
  request: jsonBody(InviteCreateRequest),
  responses: {
    ...created("Invite created + SES mail sent", Invite),
    409: problemResponse("INVITE_PENDING (one live invite per email) / ALREADY_MEMBER"),
  },
});
route({
  method: "post",
  path: "/v1/invites/{id}/resend",
  operationId: "resendInvite",
  summary: "Rotate token + resend (max 5)",
  permission: "identity.invite_member",
  request: { params: IdParam },
  responses: ok("Invite resent (new expiry)", Invite),
});
route({
  method: "post",
  path: "/v1/invites/{id}/revoke",
  operationId: "revokeInvite",
  summary: "Revoke a pending invite",
  permission: "identity.invite_member",
  request: { params: IdParam },
  responses: ok("Invite revoked", Invite),
});

registry.registerPath({
  method: "get",
  path: "/v1/invites/lookup",
  operationId: "lookupInvite",
  tags: ["identity"],
  summary: "Public invite preview (spec 16 §3.5.4)",
  description: "Resolves by token hash; invalid/expired/revoked → generic 404.",
  request: { query: z.object({ token: z.string().min(16).max(256) }) },
  responses: {
    200: {
      description: "Invite preview",
      content: { "application/json": { schema: InviteLookupResponse } },
    },
    400: undeclaredQueryRefused,
    404: problemResponse("Invalid/expired/revoked (generic)"),
    429: problemResponse("TOO_MANY_ATTEMPTS (invite_ip throttle)"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/invites/accept",
  operationId: "acceptInvite",
  tags: ["identity"],
  summary: "Accept invite (public; creates user when new — spec 16 §3.5.5)",
  description:
    "Acceptance is idempotent for an already-granted membership. Exempt from the Idempotency-Key requirement (pre-tenant public flow; spec 16 §5 note). Sets the session cookie for newly created users.",
  request: { body: { content: { "application/json": { schema: InviteAcceptRequest } } } },
  responses: {
    200: {
      description: "Membership granted (cookie set when a session was minted)",
      content: { "application/json": { schema: InviteAcceptResponse } },
    },
    400: problemResponse("Validation failed / PASSWORD_REQUIRED / PASSWORD_POLICY"),
    404: problemResponse("Invalid/expired/revoked token (generic)"),
    409: problemResponse("INVITE_STALE / EXISTING_USER_LOGIN_REQUIRED"),
    429: problemResponse("TOO_MANY_ATTEMPTS (invite_ip throttle)"),
  },
});

// --- roles + catalog ---

route({
  method: "get",
  path: "/v1/roles",
  operationId: "listRoles",
  summary: "List roles + counts",
  permission: "identity.view_roles",
  responses: ok("Roles", RoleListResponse),
});
route({
  method: "get",
  path: "/v1/roles/{id}",
  operationId: "getRole",
  summary: "Role detail + permission codes",
  permission: "identity.view_roles",
  request: { params: IdParam },
  responses: ok("Role", RoleDetail),
});
route({
  method: "post",
  path: "/v1/roles",
  operationId: "createRole",
  summary: "Create custom role by cloning (spec 16 §3.9.1)",
  permission: "identity.manage_roles",
  request: jsonBody(RoleCreateRequest),
  responses: created("Role created", RoleDetail),
});
route({
  method: "patch",
  path: "/v1/roles/{id}",
  operationId: "updateRole",
  summary: "Rename/describe a custom role",
  permission: "identity.manage_roles",
  request: { params: IdParam, ...jsonBody(RolePatchRequest) },
  responses: ok("Role updated", RoleDetail),
});
route({
  method: "patch",
  path: "/v1/roles/{id}/permissions",
  operationId: "updateRolePermissions",
  summary: "Grant/revoke permission codes (spec 16 §3.9.2–.3 guards)",
  permission: "identity.manage_roles",
  request: { params: IdParam, ...jsonBody(RolePermissionsPatchRequest) },
  responses: ok("Role permission set", RoleDetail),
});
route({
  method: "delete",
  path: "/v1/roles/{id}",
  operationId: "deleteRole",
  summary: "Delete a custom role (409 ROLE_IN_USE while referenced)",
  description:
    "Spec 16 §3.9.4 (D-608). Refused while any active or revoked membership, any invite in any status, or another role cloned from it names the role. A revoked membership and an invite in any status are history and keep the role for good, and the delete also drops the role code from the tenant's mandatory-TOTP list.",
  permission: "identity.manage_roles",
  request: { params: IdParam },
  responses: {
    204: { description: "Role deleted" },
    409: {
      description: "ROLE_IN_USE (with the counts) / SYSTEM_ROLE_IMMUTABLE",
      content: { "application/problem+json": { schema: RoleInUseProblem } },
    },
  },
});
route({
  method: "get",
  path: "/v1/permissions",
  operationId: "listPermissions",
  summary: "The permission catalog (platform_only rows filtered on tenant hosts)",
  responses: ok("Catalog", PermissionListResponse),
  description: "Session-gated: any authenticated member reads the catalog.",
});

// --- auth additions ---

registry.registerPath({
  method: "get",
  path: "/v1/auth/tenants",
  operationId: "listOwnTenants",
  tags: ["identity"],
  summary: "Memberships for the tenant picker (spec 16 §3.7.1)",
  security: sessionSecurity,
  responses: {
    200: {
      description: "Tenants of the session user",
      content: { "application/json": { schema: TenantPickerResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: passwordChangeRequired,
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/auth/profile",
  operationId: "getOwnProfile",
  tags: ["identity"],
  summary: "The session user's profile and saved language (spec 16 §3.10)",
  security: sessionSecurity,
  responses: {
    200: {
      description: "Profile of the session user",
      content: { "application/json": { schema: AuthProfile } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: passwordChangeRequired,
  },
});

registry.registerPath({
  method: "patch",
  path: "/v1/auth/profile",
  operationId: "updateOwnProfile",
  tags: ["identity"],
  summary: "Update the session user's name, language or phone (spec 16 §3.10)",
  description:
    "Writes the named fields of the session user's row and records auth_events(profile_updated) with the changed field names, never their values. Email, Telegram replies and documents addressed to the user follow the saved locale from the next message. /v1/auth/* Idempotency-Key exemption applies: the write sets values, so a repeat is the same write.",
  security: sessionSecurity,
  request: { body: { content: { "application/json": { schema: AuthProfilePatchRequest } } } },
  responses: {
    200: {
      description: "The profile after the change",
      content: { "application/json": { schema: AuthProfile } },
    },
    400: problemResponse(
      "VALIDATION_FAILED: an unsupported locale, a malformed phone, or no field named",
    ),
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "PASSWORD_CHANGE_REQUIRED, or IMPERSONATION_READ_ONLY for a read-only impersonation session",
    ),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/password/change",
  operationId: "changePassword",
  tags: ["identity"],
  summary: "Authenticated password change (spec 16 §3.4.5)",
  description:
    "Verifies the current password (+ TOTP code when enrolled), applies the policy, revokes every other session, clears the §3.5a must_change_password gate. /v1/auth/* Idempotency-Key exemption applies.",
  security: sessionSecurity,
  request: { body: { content: { "application/json": { schema: ChangePasswordRequest } } } },
  responses: {
    204: { description: "Password changed; other sessions revoked" },
    400: problemResponse("Validation failed / PASSWORD_POLICY"),
    401: problemResponse("INVALID_CREDENTIALS (wrong current password or TOTP)"),
    403: problemResponse("IMPERSONATION_READ_ONLY for a read-only impersonation session"),
  },
});
